import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './onboarding.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, API_BASE_URL, type CollectionPage, type Widget, type WidgetLayout } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: OnboardingPage,
})

const STEPS = [
  { number: 1, label: 'Collection Page' },
  { number: 2, label: 'Widget' },
  { number: 3, label: 'Share' },
]

const LAYOUT_OPTIONS: { value: WidgetLayout; icon: string; name: string; description: string }[] = [
  { value: 'grid', icon: '\uD83E\uDDF1', name: 'Grid', description: 'Wall of love layout' },
  {
    value: 'carousel',
    icon: '\uD83C\uDFA0',
    name: 'Carousel',
    description: 'Sliding testimonials',
  },
  { value: 'list', icon: '\uD83D\uDCCB', name: 'List', description: 'Vertical stack' },
]

function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(1)
  const [collectionPage, setCollectionPage] = useState<CollectionPage | null>(null)
  const [widget, setWidget] = useState<Widget | null>(null)

  // Step 1 form state
  const [pageName, setPageName] = useState('')
  const [pageHeadline, setPageHeadline] = useState('')

  // Step 2 form state
  const [selectedLayout, setSelectedLayout] = useState<WidgetLayout>('grid')

  // Shared state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const handleCreateCollectionPage = async () => {
    if (!pageName.trim()) {
      setError('Collection page name is required')
      return
    }
    if (!pageHeadline.trim()) {
      setError('Headline is required')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)
      const response = await api.createCollectionPage({
        name: pageName.trim(),
        headline: pageHeadline.trim(),
      })
      setCollectionPage(response.data)
      setCurrentStep(2)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection page')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCreateWidget = async () => {
    if (!collectionPage) return

    try {
      setIsSubmitting(true)
      setError(null)
      const response = await api.createWidget({
        name: `${collectionPage.name} Widget`,
        layout: selectedLayout,
        collectionPageIds: [collectionPage.id],
      })
      setWidget(response.data)
      setCurrentStep(3)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create widget')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      // Fallback: select text in a temp textarea
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }

  const collectionPageUrl = collectionPage ? `${API_BASE_URL}/c/${collectionPage.slug}` : ''
  const embedCode = widget
    ? `<script src="${API_BASE_URL}/embed/widget.js" data-widget-id="${widget.id}"></script>`
    : ''

  return (
    <AppLayout title="Getting Started">
      <div className={styles.onboarding}>
        {/* Step Indicator */}
        <div className={styles.stepIndicator}>
          {STEPS.map((step, index) => (
            <div key={step.number} style={{ display: 'flex', alignItems: 'center' }}>
              {index > 0 && (
                <div
                  className={`${styles.stepLine} ${currentStep > step.number - 1 ? styles.stepLineCompleted : ''}`}
                />
              )}
              <div className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep === step.number
                      ? styles.stepCircleActive
                      : currentStep > step.number
                        ? styles.stepCircleCompleted
                        : ''
                  }`}
                >
                  {currentStep > step.number ? '\u2713' : step.number}
                </div>
                <span className={styles.stepLabel}>{step.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Error display */}
        {error && (
          <Alert variant="danger" style={{ marginBottom: '1rem' }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Create Collection Page */}
        {currentStep === 1 && (
          <Card>
            <CardBody>
              <div className={styles.stepContent}>
                <h2 className={styles.stepTitle}>Create Your Collection Page</h2>
                <p className={styles.stepDescription}>
                  A collection page is where your customers will submit testimonials. Give it a name
                  and a headline that welcomes them.
                </p>

                <div className={styles.formGroup}>
                  <Input
                    label="Page Name"
                    type="text"
                    value={pageName}
                    onChange={(e) => setPageName(e.target.value)}
                    placeholder="e.g., Customer Feedback"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <Input
                    label="Headline"
                    type="text"
                    value={pageHeadline}
                    onChange={(e) => setPageHeadline(e.target.value)}
                    placeholder="e.g., We'd love to hear from you!"
                    required
                  />
                </div>
              </div>

              <div className={styles.stepActionsEnd}>
                <Button
                  variant="primary"
                  onClick={() => void handleCreateCollectionPage()}
                  loading={isSubmitting}
                >
                  Create &amp; Continue
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Step 2: Create Widget */}
        {currentStep === 2 && (
          <Card>
            <CardBody>
              <div className={styles.stepContent}>
                <h2 className={styles.stepTitle}>Choose a Widget Layout</h2>
                <p className={styles.stepDescription}>
                  Select how you want to display testimonials on your website. You can change this
                  later.
                </p>

                <div className={styles.layoutOptions}>
                  {LAYOUT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.layoutOption} ${selectedLayout === option.value ? styles.layoutOptionSelected : ''}`}
                      onClick={() => setSelectedLayout(option.value)}
                    >
                      <span className={styles.layoutIcon}>{option.icon}</span>
                      <span className={styles.layoutName}>{option.name}</span>
                      <span className={styles.layoutDesc}>{option.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.stepActions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setCurrentStep(1)
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleCreateWidget()}
                  loading={isSubmitting}
                >
                  Create Widget
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Step 3: Share */}
        {currentStep === 3 && (
          <Card>
            <CardBody>
              <div className={styles.stepContent}>
                <div className={styles.successIcon}>&#10003;</div>
                <h2 className={styles.successTitle}>You&apos;re all set!</h2>
                <p className={styles.successDescription}>
                  Your collection page and widget are ready. Share the link with your customers and
                  embed the widget on your site.
                </p>

                {/* Collection Page URL */}
                <div className={styles.shareLabel}>Collection Page URL</div>
                <div className={styles.shareUrl}>
                  <span className={styles.shareUrlText}>{collectionPageUrl}</span>
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={() => void handleCopy(collectionPageUrl, 'url')}
                  >
                    {copiedField === 'url' ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Embed Code */}
                <div className={styles.embedSection}>
                  <div className={styles.shareLabel}>Widget Embed Code</div>
                  <div className={styles.codeBlock}>{embedCode}</div>
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={() => void handleCopy(embedCode, 'embed')}
                  >
                    {copiedField === 'embed' ? 'Copied!' : 'Copy Embed Code'}
                  </button>
                </div>
              </div>

              <div className={styles.stepActionsEnd}>
                <Link to="/dashboard">
                  <Button variant="primary">Go to Dashboard</Button>
                </Link>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
